import { useContext, useEffect } from "react";
import { useToggle, useMeasure, useKeyPress } from "react-use";
import { useTransition, animated } from "react-spring";
import SETTINGS from "../settings";

import SitePlanContext from "../utils/siteplan-context";
import { ACTIONS } from "../utils/site-storage";

import classNames from "classnames";
import log from "loglevel";

import TimeAgo from "react-timeago";
import { formatDate, formatTime } from "../utils/convertor.js";
import { FA, FW } from "../utils/font-awesome.js";

const siteSelector = (props) => {
  const sitePlanContext = useContext(SitePlanContext);

  const [altKey] = useKeyPress(SETTINGS.KEYBOARD.ALT_KEY);

  const [showList, toggleList] = useToggle(false);
  const [showDeleted, toggleDeleted] = useToggle(false);

  const [listRef, { height: listHeight }] = useMeasure();
  const listTransition = useTransition(showList, null, {
    config: SETTINGS.ANIMATION.DEFAULT,
    from: { height: 0, overflow: "hidden" },
    enter: { height: listHeight },
    update: { height: listHeight },
    leave: { height: 0 },
  });

  const [delRef, { height: delHeight }] = useMeasure();
  const delTransition = useTransition(
    showDeleted && sitePlanContext.allSites.deleted.length > 0,
    null,
    {
      config: SETTINGS.ANIMATION.DEFAULT,
      from: { height: 0, overflow: "hidden" },
      enter: { height: delHeight },
      update: { height: delHeight },
      leave: { height: 0 },
    }
  );

  useEffect(() => {
    if (!showList) toggleDeleted(false);
  }, [showList]);

  const saveTextChange = (ev) => {
    let updatedMeta = { ...sitePlanContext.currentSite.meta };
    updatedMeta[
      ev.currentTarget.attributes.name.value
    ] = ev.currentTarget.innerText.trim();
    sitePlanContext.dispatch({
      type: ACTIONS.UPDATE_CONTENT,
      payload: { ...sitePlanContext.currentSite, meta: updatedMeta },
    });
  };

  const timeFormat = (value, unit, suffix, epochSeconds, nextFormatter) => {
    const dt = new Date(epochSeconds);

    if (altKey || epochSeconds < Date.now() - 1000 * 60 * 60 * 24 * 3) {
      return formatDate(dt);
    }

    switch (unit) {
      case "second":
        return "Just now";
      case "day":
        return value == 1 ? "Yesterday, " + formatTime(dt) : nextFormatter();
      default:
        return nextFormatter();
    }
  };

  return (
    <div className={classNames("site-selector", props.className)}>
      <div className="current">
        <h5
          className={classNames("title")}
          name="title"
          data-placeholder="Unnamed Site"
          contentEditable={true}
          onBlur={saveTextChange}
          suppressContentEditableWarning={true}
        >
          {sitePlanContext.currentSite.meta.title}
        </h5>
        <div className="subtitle">
          <small
            name="subtitle"
            data-placeholder="Unknown location"
            className={classNames("subtitle")}
            contentEditable={true}
            onBlur={saveTextChange}
            suppressContentEditableWarning={true}
          >
            {sitePlanContext.currentSite.meta.subtitle}
          </small>
          <div className="time">
            <span>
              <FA icon="save" />{" "}
              <TimeAgo
                formatter={timeFormat}
                date={sitePlanContext.currentSite.meta.modified}
              />
            </span>
            {altKey && (
              <span>
                <FA icon="plus-circle" />{" "}
                <TimeAgo
                  formatter={timeFormat}
                  date={sitePlanContext.currentSite.meta.created}
                />
              </span>
            )}
          </div>
        </div>
      </div>
      {listTransition.map(
        ({ item, key, props }) =>
          item && (
            <animated.div style={props} key={key}>
              <div ref={listRef}>
                {sitePlanContext.allSites.all.length > 1 && (
                  <div className="full-list">
                    <ul>
                      {sitePlanContext.allSites.all.map(
                        (item, i) =>
                          i != sitePlanContext.allSites.current && (
                            <li className="site" key={i}>
                              <div
                                className="select"
                                onClick={() => {
                                  toggleList(false);
                                  sitePlanContext.dispatch({
                                    type: ACTIONS.SWITCH_CURRENT,
                                    id: i,
                                  });
                                }}
                              >
                                <div>{item.meta.title || "Unnamed Site"}</div>
                                <div>
                                  <small>{item.meta.subtitle}</small>
                                </div>
                                <div className="time">
                                  <span>
                                    <FA icon="save" />{" "}
                                    <TimeAgo
                                      formatter={timeFormat}
                                      date={item.meta.modified}
                                    />
                                  </span>
                                  {altKey && (
                                    <span>
                                      <FA icon="plus-circle" />{" "}
                                      <TimeAgo
                                        formatter={timeFormat}
                                        date={item.meta.created}
                                      />
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                className="delete"
                                onClick={() =>
                                  sitePlanContext.dispatch({
                                    type: ACTIONS.DELETE,
                                    id: i,
                                  })
                                }
                              >
                                <FW icon="trash" />
                              </button>
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                )}
                {sitePlanContext.allSites.deleted.length > 0 && (
                  <div className="show-toggle" onClick={toggleDeleted}>
                    <span>
                      {showDeleted ? "Hide" : "Show"}{" "}
                      {sitePlanContext.allSites.deleted.length} deleted{" "}
                      {sitePlanContext.allSites.deleted.length > 1
                        ? "items"
                        : "item"}
                    </span>
                  </div>
                )}
                {delTransition.map(
                  ({ item, key, props }) =>
                    item && (
                      <animated.div style={props} key={key}>
                        <div ref={delRef}>
                          {sitePlanContext.allSites.deleted.length > 0 && (
                            <div className="full-list">
                              <ul>
                                {sitePlanContext.allSites.deleted.map(
                                  (item, i) => (
                                    <li className="site deleted" key={i}>
                                      <div className="select">
                                        <div>
                                          {item.meta.title || "Unnamed Site"}
                                        </div>
                                        <div>
                                          <small>{item.meta.subtitle}</small>
                                        </div>
                                        <div className="time">
                                          <span>
                                            <FA icon="trash" />{" "}
                                            <TimeAgo
                                              formatter={timeFormat}
                                              date={item.meta.deleted}
                                            />
                                          </span>
                                          {altKey && (
                                            <span>
                                              <FA icon="save" />{" "}
                                              <TimeAgo
                                                formatter={timeFormat}
                                                date={item.meta.modified}
                                              />
                                            </span>
                                          )}
                                          {altKey && (
                                            <span>
                                              <FA icon="plus-circle" />{" "}
                                              <TimeAgo
                                                formatter={timeFormat}
                                                date={item.meta.created}
                                              />
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        className="undo"
                                        onClick={() =>
                                          sitePlanContext.dispatch({
                                            type: ACTIONS.UNDELETE,
                                            id: i,
                                          })
                                        }
                                      >
                                        <FW icon="recycle" />
                                      </button>
                                    </li>
                                  )
                                )}
                              </ul>
                              <div
                                className="footer-link"
                                onClick={() => {
                                  if (
                                    confirm(
                                      "Clear all deleted items? \nThis cannot be undone."
                                    )
                                  ) {
                                    toggleDeleted(false);
                                    sitePlanContext.dispatch({
                                      type: ACTIONS.PURGE_ALL,
                                    });
                                  }
                                }}
                              >
                                <FA icon="skull" />
                                Clear deleted items
                              </div>
                            </div>
                          )}
                        </div>
                      </animated.div>
                    )
                )}
              </div>
            </animated.div>
          )
      )}
      <div className="buttons">
        {(sitePlanContext.allSites.all.length > 1 ||
          sitePlanContext.allSites.deleted.length > 0) && (
          <span onClick={toggleList}>
            <FW icon="list" /> {showList ? "Hide" : "Show"}
          </span>
        )}
        {altKey ? (
          <span
            title="Make a copy of the current site"
            onClick={() => {
              toggleList(false);
              sitePlanContext.dispatch({ type: ACTIONS.COPY });
            }}
          >
            <FW icon="copy" /> Copy
          </span>
        ) : (
          <span
            title="Hold Alt/Option to duplicate"
            onClick={() => {
              toggleList(false);
              sitePlanContext.dispatch({ type: ACTIONS.NEW });
            }}
          >
            <FW icon="plus-circle" /> New
          </span>
        )}
      </div>
      {/* <div className="buttons">
        <span title="Delete this terrain" onClick={() => {
          sitePlanContext.dispatch({type:ACTIONS.DELETE_HEIGHT_MAP});
        }}><FW icon="mountain" /> Delete</span>
        <span title="Delete all terrain" onClick={() => {
          sitePlanContext.dispatch({type:ACTIONS.DELETE_ALL_HEIGHT_MAPS});
        }}><FW icon="snowplow" /> Delete</span>
        <span title="Delete all sites" onClick={() => {
          sitePlanContext.dispatch({type:ACTIONS.DELETE_EVERYTHING});
        }}><FW icon="bomb" /> Delete</span>
      </div> */}
    </div>
  );
};

export default siteSelector;
